import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { MobileLayout } from "@/components/Layout";
import { toast } from "sonner";
import {
  FileText, Users, Building2, Scale, ClipboardList,
  Save, Loader2, Calendar, Hash, MapPin, Clock,
  ChevronLeft, ChevronRight, Download, ShieldCheck,
  Copy, Lock, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import SuiviLotsPanel from "@/components/SuiviLotsPanel";
import { useOffline } from "@/lib/OfflineContext";

const LEGAL_TEXT = `LES ENTREPRISES TITULAIRES DES MARCHÉS SONT TENUES D'ÊTRE PRÉSENTES LORSQU'ELLES SONT CONVOQUÉES. À DÉFAUT, DES PÉNALITÉS D'ABSENCE EN RÉUNION DE CHANTIER POURRONT ÊTRE APPLIQUÉES À HAUTEUR DE 70€ SANS JUSTIFICATIF. ELLES SONT TENUES DE LIRE ET APPLIQUER LES MODIFICATIONS NÉCESSAIRES ET DE RÉPONDRE AU MAÎTRE D'ŒUVRE DANS LES DÉLAIS IMPARTIS.`;

const CompteRenduPage = () => {
  const { crId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cr, setCr] = useState(null);
  const [projet, setProjet] = useState(null);
  const [acteurs, setActeurs] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("entete");
  const [showValidateDialog, setShowValidateDialog] = useState(false);
  const [validating, setValidating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const { isOffline } = useOffline();

  useEffect(() => { loadCR(); }, [crId]);

  const loadCR = async () => {
    try {
      const res = await api.get(`/comptes-rendus/${crId}`);
      setCr(res.data);
      setProjet(res.data.projet);
      setActeurs(res.data.acteurs);
      setEntreprises(res.data.entreprises);
    } catch (e) {
      toast.error("Erreur de chargement du compte rendu");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const saveHeader = async () => {
    setSaving(true);
    try {
      await api.put(`/comptes-rendus/${crId}`, {
        numero: cr.numero,
        date_cr: cr.date_cr,
        date_prochaine_reunion: cr.date_prochaine_reunion,
        statut: cr.statut
      });
      toast.success("En-tête sauvegardé");
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const saveMentions = async () => {
    setSaving(true);
    try {
      await api.put(`/comptes-rendus/${crId}`, {
        informations_complementaires: cr.informations_complementaires || ""
      });
      toast.success("Informations complémentaires sauvegardées");
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const saveActeurs = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/comptes-rendus/${crId}/acteurs`, acteurs);
      setActeurs(res.data);
      toast.success("Acteurs sauvegardés");
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const saveEntreprises = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/comptes-rendus/${crId}/entreprises`, entreprises);
      setEntreprises(res.data);
      toast.success("Entreprises sauvegardées");
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const updateActeur = (idx, field, value) => {
    setActeurs(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const updateEntreprise = (idx, field, value) => {
    setEntreprises(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  const isValidated = cr?.statut === "validé";
  const isReadOnly = isValidated;

  const handleValidate = async () => {
    setValidating(true);
    try {
      const res = await api.put(`/comptes-rendus/${crId}/valider`);
      setCr(res.data);
      setShowValidateDialog(false);
      toast.success("Compte rendu validé et verrouillé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setValidating(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/comptes-rendus/${crId}/pdf`, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `CR_${cr?.numero || ""}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const res = await api.post(`/comptes-rendus/${crId}/dupliquer`);
      toast.success(`Nouveau CR #${res.data.numero} créé depuis le CR #${cr?.numero}`);
      navigate(`/comptes-rendus/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la duplication");
    } finally {
      setDuplicating(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout title="Chargement..." showBack>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-orange-500" size={32} />
        </div>
      </MobileLayout>
    );
  }

  const tabItems = [
    { value: "entete", icon: FileText, label: "En-tête" },
    { value: "acteurs", icon: Users, label: "Acteurs" },
    { value: "entreprises", icon: Building2, label: "Entrep." },
    { value: "suivi", icon: ClipboardList, label: "Suivi" },
    { value: "mentions", icon: Scale, label: "Mentions" },
  ];

  const tabIdx = tabItems.findIndex(t => t.value === activeTab);
  const canPrev = tabIdx > 0;
  const canNext = tabIdx < tabItems.length - 1;

  return (
    <MobileLayout
      title={`CR #${cr?.numero || ""}`}
      showBack
      onBack={() => navigate(`/projects/${cr?.projet_id}`)}
      rightContent={
        <Badge
          variant={cr?.statut === "validé" ? "default" : "secondary"}
          className={cr?.statut === "validé"
            ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
            : "bg-zinc-800 text-zinc-400 border border-zinc-700"
          }
          data-testid="cr-status-badge"
        >
          {cr?.statut}
        </Badge>
      }
    >
      <div className="px-4 lg:px-6 py-4 space-y-4 animate-fade-in" data-testid="cr-page">
        {/* Bandeau verrouillé */}
        {isValidated && (
          <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-3" data-testid="cr-validated-banner">
            <Lock size={18} className="text-emerald-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-emerald-300 text-sm font-semibold">Compte rendu validé et verrouillé</p>
              <p className="text-emerald-500/80 text-xs">
                Validé le {cr.validated_at?.slice(0, 10)} par {cr.validated_by_name || "—"}
              </p>
            </div>
          </div>
        )}

        {/* Bandeau en-tête projet */}
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-lg p-3 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="cr-header-banner">
          <span className="text-orange-400 font-bold">{projet?.name}</span>
          <span className="text-zinc-500 flex items-center gap-1"><MapPin size={12} />{projet?.address}</span>
          <span className="text-zinc-500 flex items-center gap-1"><Hash size={12} />CR n°{cr?.numero}</span>
          <span className="text-zinc-500 flex items-center gap-1"><Calendar size={12} />{cr?.date_cr}</span>
        </div>

        {/* Barre d'actions */}
        <div className="flex flex-wrap gap-2" data-testid="cr-actions-bar">
          <Button
            onClick={handleDownloadPDF}
            disabled={downloading || isOffline}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:text-white hover:border-orange-500/50 gap-2 flex-1 sm:flex-none"
            data-testid="download-pdf-btn"
            title={isOffline ? "Indisponible hors-ligne" : ""}
          >
            {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Télécharger PDF
          </Button>

          {!isValidated && (
            <Button
              onClick={() => setShowValidateDialog(true)}
              disabled={isOffline}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 flex-1 sm:flex-none"
              data-testid="validate-cr-btn"
              title={isOffline ? "Indisponible hors-ligne" : ""}
            >
              <ShieldCheck size={16} />
              Valider et approuver
            </Button>
          )}

          <Button
            onClick={handleDuplicate}
            disabled={duplicating || isOffline}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:text-white hover:border-orange-500/50 gap-2 flex-1 sm:flex-none"
            data-testid="duplicate-cr-btn"
            title={isOffline ? "Indisponible hors-ligne" : "Crée un nouveau CR en reprenant les tâches non terminées"}
          >
            {duplicating ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}
            Nouveau CR depuis celui-ci
          </Button>
        </div>

        {/* Onglets */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-zinc-900 border border-zinc-800 h-auto p-1 grid grid-cols-5" data-testid="cr-tabs">
            {tabItems.map(t => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="flex flex-col items-center gap-0.5 py-2 px-1 text-xs data-[state=active]:bg-orange-500/15 data-[state=active]:text-orange-400"
                data-testid={`tab-${t.value}`}
              >
                <t.icon size={16} />
                <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* === ONGLET EN-TÊTE === */}
          <TabsContent value="entete" data-testid="tab-content-entete">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
              <h3 className="text-white font-bold text-base tracking-wide uppercase flex items-center gap-2">
                <FileText size={18} className="text-orange-500" /> En-tête du compte rendu
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nom de l&apos;opération</Label>
                  <Input value={projet?.name || ""} readOnly className="input-dark bg-zinc-800/50 text-zinc-400 cursor-default" data-testid="input-operation" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Adresse</Label>
                  <Input value={projet?.address || ""} readOnly className="input-dark bg-zinc-800/50 text-zinc-400 cursor-default" data-testid="input-adresse" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">N° compte rendu</Label>
                  <Input
                    type="number"
                    value={cr?.numero || ""}
                    onChange={e => setCr(p => ({ ...p, numero: parseInt(e.target.value) || 0 }))}
                    className="input-dark"
                    readOnly={isReadOnly}
                    data-testid="input-numero"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Date du CR</Label>
                  <Input
                    type="date"
                    value={cr?.date_cr || ""}
                    onChange={e => setCr(p => ({ ...p, date_cr: e.target.value }))}
                    className="input-dark"
                    readOnly={isReadOnly}
                    data-testid="input-date-cr"
                  />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Date et heure prochaine réunion</Label>
                  <Input
                    type="datetime-local"
                    value={cr?.date_prochaine_reunion || ""}
                    onChange={e => setCr(p => ({ ...p, date_prochaine_reunion: e.target.value }))}
                    className="input-dark"
                    readOnly={isReadOnly}
                    data-testid="input-prochaine-reunion"
                  />
                </div>
              </div>

              {!isReadOnly && (
                <Button onClick={saveHeader} disabled={saving} className="btn-primary w-full sm:w-auto gap-2" data-testid="save-entete">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Sauvegarder l&apos;en-tête
                </Button>
              )}
            </div>
          </TabsContent>

          {/* === ONGLET ACTEURS === */}
          <TabsContent value="acteurs" data-testid="tab-content-acteurs">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
              <h3 className="text-white font-bold text-base tracking-wide uppercase flex items-center gap-2">
                <Users size={18} className="text-orange-500" /> Acteurs (MOA / AMO / BET)
              </h3>

              <div className="overflow-x-auto -mx-4 px-4">
                <table className="w-full min-w-[700px] text-sm" data-testid="acteurs-table">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[200px]">Rôle</th>
                      <th className="text-left py-2 px-2 text-zinc-500 font-medium text-xs uppercase">Nom / Société</th>
                      <th className="text-left py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[120px]">Téléphone</th>
                      <th className="text-left py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[160px]">Email</th>
                      <th className="text-center py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[50px]">Conv.</th>
                      <th className="text-center py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[50px]">Abs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acteurs.map((a, i) => (
                      <tr
                        key={a.id}
                        className={`border-b border-zinc-800/50 transition-colors ${a.absent ? "bg-yellow-500/10" : "hover:bg-zinc-800/30"}`}
                        data-testid={`acteur-row-${i}`}
                      >
                        <td className="py-2 px-2">
                          <span className={`text-xs font-medium ${a.absent ? "text-yellow-400" : "text-zinc-300"}`}>{a.role}</span>
                        </td>
                        <td className="py-1 px-1">
                          <Input
                            value={a.nom}
                            onChange={e => updateActeur(i, "nom", e.target.value)}
                            className="input-dark h-8 text-sm border-transparent focus:border-orange-500/50"
                            readOnly={isReadOnly}
                            data-testid={`acteur-nom-${i}`}
                          />
                        </td>
                        <td className="py-1 px-1">
                          <Input
                            value={a.telephone}
                            onChange={e => updateActeur(i, "telephone", e.target.value)}
                            placeholder="06..."
                            className="input-dark h-8 text-sm border-transparent focus:border-orange-500/50"
                            readOnly={isReadOnly}
                            data-testid={`acteur-tel-${i}`}
                          />
                        </td>
                        <td className="py-1 px-1">
                          <Input
                            type="email"
                            value={a.email}
                            onChange={e => updateActeur(i, "email", e.target.value)}
                            placeholder="email@..."
                            className="input-dark h-8 text-sm border-transparent focus:border-orange-500/50"
                            readOnly={isReadOnly}
                            data-testid={`acteur-email-${i}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Checkbox
                            checked={a.convoque}
                            onCheckedChange={v => updateActeur(i, "convoque", !!v)}
                            disabled={isReadOnly}
                            data-testid={`acteur-conv-${i}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Checkbox
                            checked={a.absent}
                            onCheckedChange={v => updateActeur(i, "absent", !!v)}
                            disabled={isReadOnly}
                            className={a.absent ? "border-yellow-500 data-[state=checked]:bg-yellow-500" : ""}
                            data-testid={`acteur-abs-${i}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <div className="w-3 h-3 rounded bg-yellow-500/20 border border-yellow-500/30"></div>
                  Absent = surligné en jaune
                </div>
              </div>

              {!isReadOnly && (
                <Button onClick={saveActeurs} disabled={saving} className="btn-primary w-full sm:w-auto gap-2" data-testid="save-acteurs">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Sauvegarder les acteurs
                </Button>
              )}
            </div>
          </TabsContent>

          {/* === ONGLET ENTREPRISES === */}
          <TabsContent value="entreprises" data-testid="tab-content-entreprises">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
              <h3 className="text-white font-bold text-base tracking-wide uppercase flex items-center gap-2">
                <Building2 size={18} className="text-orange-500" /> Entreprises par lot
              </h3>

              <div className="overflow-x-auto -mx-4 px-4">
                <table className="w-full min-w-[700px] text-sm" data-testid="entreprises-table">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[180px]">Lot</th>
                      <th className="text-left py-2 px-2 text-zinc-500 font-medium text-xs uppercase">Nom / Société</th>
                      <th className="text-left py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[120px]">Téléphone</th>
                      <th className="text-left py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[160px]">Email</th>
                      <th className="text-center py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[50px]">Conv.</th>
                      <th className="text-center py-2 px-2 text-zinc-500 font-medium text-xs uppercase w-[50px]">Abs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entreprises.map((e, i) => (
                      <tr
                        key={e.id}
                        className={`border-b border-zinc-800/50 transition-colors ${e.absent ? "bg-orange-500/10" : "hover:bg-zinc-800/30"}`}
                        data-testid={`entreprise-row-${i}`}
                      >
                        <td className="py-2 px-2">
                          <span className={`text-xs font-medium ${e.absent ? "text-orange-400" : "text-zinc-300"}`}>{e.lot}</span>
                        </td>
                        <td className="py-1 px-1">
                          <Input
                            value={e.nom}
                            onChange={ev => updateEntreprise(i, "nom", ev.target.value)}
                            placeholder="Nom entreprise..."
                            className="input-dark h-8 text-sm border-transparent focus:border-orange-500/50"
                            readOnly={isReadOnly}
                            data-testid={`ent-nom-${i}`}
                          />
                        </td>
                        <td className="py-1 px-1">
                          <Input
                            value={e.telephone}
                            onChange={ev => updateEntreprise(i, "telephone", ev.target.value)}
                            placeholder="06..."
                            className="input-dark h-8 text-sm border-transparent focus:border-orange-500/50"
                            readOnly={isReadOnly}
                            data-testid={`ent-tel-${i}`}
                          />
                        </td>
                        <td className="py-1 px-1">
                          <Input
                            type="email"
                            value={e.email}
                            onChange={ev => updateEntreprise(i, "email", ev.target.value)}
                            placeholder="email@..."
                            className="input-dark h-8 text-sm border-transparent focus:border-orange-500/50"
                            readOnly={isReadOnly}
                            data-testid={`ent-email-${i}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Checkbox
                            checked={e.convoque}
                            onCheckedChange={v => updateEntreprise(i, "convoque", !!v)}
                            disabled={isReadOnly}
                            data-testid={`ent-conv-${i}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Checkbox
                            checked={e.absent}
                            onCheckedChange={v => updateEntreprise(i, "absent", !!v)}
                            disabled={isReadOnly}
                            className={e.absent ? "border-orange-500 data-[state=checked]:bg-orange-500" : ""}
                            data-testid={`ent-abs-${i}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <div className="w-3 h-3 rounded bg-orange-500/20 border border-orange-500/30"></div>
                  Absent = surligné en orange
                </div>
              </div>

              {!isReadOnly && (
                <Button onClick={saveEntreprises} disabled={saving} className="btn-primary w-full sm:w-auto gap-2" data-testid="save-entreprises">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Sauvegarder les entreprises
                </Button>
              )}
            </div>
          </TabsContent>

          {/* === ONGLET SUIVI PAR LOTS === */}
          <TabsContent value="suivi" data-testid="tab-content-suivi">
            <SuiviLotsPanel crId={crId} readOnly={isReadOnly} />
          </TabsContent>

          {/* === ONGLET MENTIONS LÉGALES === */}
          <TabsContent value="mentions" data-testid="tab-content-mentions">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
              <h3 className="text-white font-bold text-base tracking-wide uppercase flex items-center gap-2">
                <Scale size={18} className="text-orange-500" /> Mentions légales obligatoires
              </h3>
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid="legal-text-block">
                <p className="text-zinc-300 text-sm leading-relaxed font-medium">
                  {LEGAL_TEXT}
                </p>
              </div>
              <p className="text-zinc-600 text-xs italic">
                Ce texte est affiché automatiquement en page de garde de chaque compte rendu et sera imprimé en première page du PDF.
              </p>

              {/* Informations complémentaires */}
              <div className="border-t border-zinc-800 pt-4 space-y-2">
                <h4 className="text-white font-semibold text-sm tracking-wide uppercase flex items-center gap-2">
                  <FileText size={16} className="text-orange-500" /> Informations complémentaires
                </h4>
                <p className="text-zinc-600 text-xs">
                  Texte libre ajouté après les mentions légales dans le PDF généré.
                </p>
                <textarea
                  value={cr?.informations_complementaires || ""}
                  onChange={e => setCr(p => ({ ...p, informations_complementaires: e.target.value }))}
                  placeholder="Ajoutez ici toute information complémentaire à faire figurer sur le compte rendu..."
                  rows={4}
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500/50 resize-y"
                  readOnly={isReadOnly}
                  data-testid="input-infos-complementaires"
                />
                {!isReadOnly && (
                  <Button onClick={saveMentions} disabled={saving} className="btn-primary w-full sm:w-auto gap-2" data-testid="save-mentions">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Sauvegarder les informations
                  </Button>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Navigation entre onglets */}
        <div className="flex items-center justify-between pt-2 pb-4">
          <Button
            variant="outline"
            onClick={() => canPrev && setActiveTab(tabItems[tabIdx - 1].value)}
            disabled={!canPrev}
            className="border-zinc-700 text-zinc-400 hover:text-white gap-1"
            data-testid="prev-tab-btn"
          >
            <ChevronLeft size={16} /> Précédent
          </Button>
          <span className="text-zinc-600 text-xs">
            {tabIdx + 1} / {tabItems.length}
          </span>
          <Button
            variant="outline"
            onClick={() => canNext && setActiveTab(tabItems[tabIdx + 1].value)}
            disabled={!canNext}
            className="border-zinc-700 text-zinc-400 hover:text-white gap-1"
            data-testid="next-tab-btn"
          >
            Suivant <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      {/* Dialog confirmation validation */}
      <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-400" />
              Valider et verrouiller ce CR ?
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm pt-2">
              Cette action est <span className="text-amber-400 font-semibold">irréversible</span>.
              Une fois validé, le compte rendu sera verrouillé et aucune modification ne pourra être apportée
              aux en-têtes, acteurs, entreprises ou tâches.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowValidateDialog(false)}
              className="flex-1 border-zinc-700 text-zinc-400"
              data-testid="cancel-validate-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={handleValidate}
              disabled={validating}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              data-testid="confirm-validate-btn"
            >
              {validating ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              Confirmer la validation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
};

export default CompteRenduPage;
